import React, { useState, useCallback, useEffect } from "react";
import {
  Input,
  Textarea,
  Button,
  ScrollArea,
  Badge,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "src/components/ui";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "src/components/ui/avatar";

import { Label } from "src/components/ui/label";
import { toast } from "sonner";

import {
  Search,
  Filter,
  X,
  MessageSquare,
  MessageCircle,
  Hash,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Volume2,
  VolumeX,
  Bot,
} from "lucide-react";

import { cn } from "src/shared/lib/utils/utils";
import { format, parseISO } from "date-fns";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequests } from "src/shared/api/api";
import {
  formatLastMessageTime,
  truncatePreview,
  isImageMessage,
  cleanMessagePreview,
} from "./utils";

const filterSchema = z.object({
  search: z.string().optional(),
  status: z.string().optional(),
  channel_id: z.string().optional(),
  contragent_id: z.string().optional(),
  created_range: z.object({
    from: z.date().optional(),
    to: z.date().optional()
  }).optional(),
  updated_range: z.object({
    from: z.date().optional(),
    to: z.date().optional()
  }).optional(),
  sort_by: z.string().default("last_message_time"),
  sort_order: z.string().default("desc"),
});

const TELEGRAM_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50"><path d="M25,2c12.703,0,23,10.297,23,23S37.703,48,25,48S2,37.703,2,25S12.297,2,25,2z M32.934,34.375	c0.423-1.298,2.405-14.234,2.65-16.783c0.074-0.772-0.17-1.285-0.648-1.514c-0.578-0.278-1.434-0.139-2.427,0.219	c-1.362,0.491-18.774,7.884-19.78,8.312c-0.954,0.405-1.856,0.847-1.856,1.487c0,0.45,0.267,0.703,1.003,0.966	c0.766,0.273,2.695,0.858,3.834,1.172c1.097,0.303,2.346,0.04,3.046-0.395c0.742-0.461,9.305-6.191,9.92-6.693	c0.614-0.502,1.104,0.141,0.602,0.644c-0.502,0.502-6.38,6.207-7.155,6.997c-0.941,0.959-0.273,1.953,0.358,2.351	c0.721,0.454,5.906,3.932,6.687,4.49c0.781,0.558,1.573,0.811,2.298,0.811C32.191,36.439,32.573,35.484,32.934,34.375z"></path></svg>`;

export default React.memo(function UserList({
  chats,
  loading,
  searchText,
  setSearchText,
  channels,
  channelsLoading = false,
  selectedChat,
  onSelectChat,
  formatTime,
  token,
  onApplyFilters,
  currentFilters,
  typingStatus,
  onlineStatus,
}) {
  const [localSearch, setLocalSearch] = useState(searchText);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [activeFilters, setActiveFilters] = useState(currentFilters || {});
  const [activeTab, setActiveTab] = useState("chats");
  const [allChannels, setAllChannels] = useState(channels || []);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [isAddChannelOpen, setIsAddChannelOpen] = useState(false);
  const [isEditChannelOpen, setIsEditChannelOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showOAuthView, setShowOAuthView] = useState(false);
  const [channelForm, setChannelForm] = useState({
    name: "",
    client_id: "",
    client_secret: "",
    bot_token: "",
  });
  const [channelsSearchQuery, setChannelsSearchQuery] = useState("");
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem("chat_sound_notifications") !== "false";
  });
  const [channelType, setChannelType] = useState("avito");
  const [connectingTelegram, setConnectingTelegram] = useState(false);

  const filterForm = useForm({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      sort_by: "last_message_time",
      sort_order: "desc",
      ...currentFilters
    },
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== searchText) setSearchText(localSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, searchText, setSearchText]);

  useEffect(() => {
    setActiveFilters(currentFilters || {});
    filterForm.reset({
      sort_by: "last_message_time",
      sort_order: "desc",
      ...currentFilters
    });
  }, [currentFilters, filterForm]);

  useEffect(() => {
    setAllChannels(channels || []);
  }, [channels]);

  const loadChannels = useCallback(async () => {
    setLoadingChannels(true);
    try {
      const response = await apiRequests.chats.getChannels({ token, skip: 0, limit: 100 });
      if (response.data && Array.isArray(response.data)) setAllChannels(response.data);
    } catch (error) {
      toast.error("Не удалось загрузить список каналов");
    } finally {
      setLoadingChannels(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAddChannelOpen) {
      setShowOAuthView(false);
      setChannelForm({ name: "", client_id: "", client_secret: "", bot_token: "" });
    }
  }, [isAddChannelOpen]);

  const handleFilterClick = () => {
    filterForm.reset({ sort_by: "last_message_time", sort_order: "desc", ...activeFilters });
    setFilterModalVisible(true);
  };

  const applyFilters = (filters) => {
    const queryParams = {
      ...filters,
      created_from: filters.created_range?.from ? format(filters.created_range.from, "yyyy-MM-dd'T'HH:mm:ss") : undefined,
      created_to: filters.created_range?.to ? format(filters.created_range.to, "yyyy-MM-dd'T'HH:mm:ss") : undefined,
      updated_from: filters.updated_range?.from ? format(filters.updated_range.from, "yyyy-MM-dd'T'HH:mm:ss") : undefined,
      updated_to: filters.updated_range?.to ? format(filters.updated_range.to, "yyyy-MM-dd'T'HH:mm:ss") : undefined,
    };
    delete queryParams.created_range;
    delete queryParams.updated_range;
    setActiveFilters(filters);
    if (onApplyFilters) onApplyFilters(queryParams);
    setFilterModalVisible(false);
  };

  const resetFilters = () => {
    filterForm.reset({ sort_by: "last_message_time", sort_order: "desc", search: "", status: "", channel_id: "", contragent_id: "", created_range: undefined, updated_range: undefined });
    setActiveFilters({});
    if (onApplyFilters) onApplyFilters({});
    setFilterModalVisible(false);
  };

  const removeFilter = (filterKey) => {
    const newFilters = { ...activeFilters };
    if (filterKey === "sort") { delete newFilters.sort_by; delete newFilters.sort_order; }
    else delete newFilters[filterKey];
    setActiveFilters(newFilters);
    if (onApplyFilters) onApplyFilters(newFilters);
  };

  
  const handleAddTelegramChannel = async () => {
    if (!channelForm.bot_token) {
      toast.error("Введите Bot Token");
      return;
    }
    setConnectingTelegram(true);
    try {
      await apiRequests.chats.connectTelegram({
        bot_token: channelForm.bot_token,
        channel_name: channelForm.name || undefined,
      });

      setIsAddChannelOpen(false);
      await loadChannels();

    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка подключения Telegram");
    } finally {
      setConnectingTelegram(false);
    }
  };

  const handleAddChannel = async () => {
    if (!channelForm.client_id || !channelForm.client_secret) {
      toast.error("Пожалуйста, заполните все поля");
      return;
    }
    try {
      await apiRequests.avito.connect({
        api_key: channelForm.client_id,
        api_secret: channelForm.client_secret,
        channel_name: channelForm.name,
        grant_type: "client_credentials",
      });
      toast.success("Канал успешно подключен!");
      setIsAddChannelOpen(false);
      setChannelForm({ name: "", client_id: "", client_secret: "", bot_token: "" });
      loadChannels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Не удалось подключить канал Avito.");
    }
  };

  const handleOAuthConnect = async () => {
    try {
      const response = await apiRequests.avito.connect({
        channel_name: channelForm.name || "Avito OAuth",
        grant_type: "authorization_code",
      });
      if (response.data?.authorization_url) {
        toast.success("Переходим к авторизации на Avito");
        window.open(response.data.authorization_url, "_blank");
        setIsAddChannelOpen(false);
        setChannelForm({ name: "", client_id: "", client_secret: "", bot_token: "" });
      } else {
        toast.error("Не удалось получить ссылку авторизации");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Не удалось получить ссылку для OAuth");
    }
  };

  const handleEditChannel = async () => {
    if (!selectedChannel) return;
    const allowedFields = ["name", "description", "svg_icon", "api_config_name", "is_active", "tags"];
    const updateData = {};
    allowedFields.forEach(field => {
      if (field in channelForm) {
        const value = channelForm[field];
        if (value !== undefined && value !== null) updateData[field] = value;
      }
    });
    if (Object.keys(updateData).length === 0) { toast.error("Нет данных для обновления"); return; }
    try {
      await apiRequests.chats.updateChannel(selectedChannel.id, updateData);
      toast.success("Канал успешно обновлен");
      setIsEditChannelOpen(false);
      setSelectedChannel(null);
      loadChannels();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || "Ошибка обновления канала";
      toast.error(typeof errorMessage === "string" ? errorMessage : JSON.stringify(errorMessage));
    }
  };

  const handleDeleteChannel = async (channelId) => {
    try {
      await apiRequests.chats.deleteChannel(channelId);
      toast.success("Канал успешно удален");
      loadChannels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка удаления канала");
    }
  };

  const handleEditClick = (channel) => {
    setSelectedChannel(channel);
    setChannelForm({
      name: channel.name || "",
      type: channel.type || "",
      description: channel.description || "",
      svg_icon: channel.svg_icon || "",
      tags: channel.tags || {},
      api_config_name: channel.api_config_name || "",
      is_active: channel.is_active !== undefined ? channel.is_active : true,
      bot_token: "",
    });
    setIsEditChannelOpen(true);
  };

  const renderActiveFilters = () => {
    if (Object.keys(activeFilters).length === 0) return null;
    const filters = [];
    if (activeFilters.channel_id) {
      const channel = allChannels.find(c => c.id.toString() === activeFilters.channel_id);
      filters.push({ key: "channel_id", label: `Канал: ${channel?.name || activeFilters.channel_id}` });
    }
    if (activeFilters.status) filters.push({ key: "status", label: `Статус: ${activeFilters.status}` });
    if (activeFilters.contragent_id) filters.push({ key: "contragent_id", label: `Контрагент: ${activeFilters.contragent_id}` });
    if (activeFilters.search) filters.push({ key: "search", label: `Поиск: ${activeFilters.search}` });
    if (activeFilters.created_range?.from || activeFilters.created_range?.to) {
      const from = activeFilters.created_range.from ? format(activeFilters.created_range.from, "dd.MM.yyyy") : "";
      const to = activeFilters.created_range.to ? format(activeFilters.created_range.to, "dd.MM.yyyy") : "";
      filters.push({ key: "created_range", label: `Дата создания: ${from} - ${to}` });
    }
    if (activeFilters.updated_range?.from || activeFilters.updated_range?.to) {
      const from = activeFilters.updated_range.from ? format(activeFilters.updated_range.from, "dd.MM.yyyy") : "";
      const to = activeFilters.updated_range.to ? format(activeFilters.updated_range.to, "dd.MM.yyyy") : "";
      filters.push({ key: "updated_range", label: `Дата обновления: ${from} - ${to}` });
    }
    const defaultSortBy = "last_message_time", defaultSortOrder = "desc";
    if (activeFilters.sort_by !== defaultSortBy || activeFilters.sort_order !== defaultSortOrder) {
      const sortByLabels = { last_message_time: "Последнему сообщению", created_at: "Дате создания", updated_at: "Дате обновления", name: "Имени" };
      const sortOrderLabels = { desc: "убывание", asc: "возрастание" };
      filters.push({ key: "sort", label: `Сортировка: ${sortByLabels[activeFilters.sort_by]} (${sortOrderLabels[activeFilters.sort_order]})` });
    }
    if (filters.length === 0) return null;
    return (
      <div className="px-4 py-2 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border-b">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-blue-600">Активные фильтры:</span>
          {filters.map(filter => (
            <Badge key={filter.key} variant="outline" className="pl-2 pr-1.5 py-1 gap-1.5 hover:bg-blue-50 cursor-pointer transition-all duration-200 border-blue-200 bg-white shadow-sm hover:shadow-md hover:border-blue-300 group" onClick={() => removeFilter(filter.key)}>
              <span className="text-xs text-blue-700">{filter.label}</span>
              <X className="h-3 w-3 text-blue-500 group-hover:text-blue-700 transition-colors" />
            </Badge>
          ))}
          <Button variant="ghost" size="sm" onClick={resetFilters} className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100/50">Сбросить все</Button>
        </div>
      </div>
    );
  };

  const getChatChannelLabel = (chat) => {
    const rawChannelName =
      chat.channel_name ||
      allChannels.find((channel) => channel.id === chat.channel_id)?.name ||
      chat.channel_type;

    if (!rawChannelName) return null;

    return rawChannelName.replace(/^Avito\s*-\s*/i, "").trim() || rawChannelName;
  };

  const renderChatItem = (chat) => {
    const isSelected = selectedChat?.id === chat.id;
    const isTyping = typingStatus[chat.id]?.isTyping || false;
    const isOnline = onlineStatus[chat.id] && Object.values(onlineStatus[chat.id]).some(u => u.userType === "CLIENT" && u.connected);
    const lastMessageTime = formatLastMessageTime(chat.last_message_time);
    const isUnread = chat.unread_count > 0;
    const channelLabel = getChatChannelLabel(chat);
    const relatedChatsCount = Number(chat.related_chats_count || 1);

    let messagePreview = null;
    let lastMessage = "Нет сообщений";
    if (chat.last_message_preview && isImageMessage(chat.last_message_preview)) {
      lastMessage = "Изображение";
    } else if (chat.last_message_preview) {
      lastMessage = truncatePreview(cleanMessagePreview(chat.last_message_preview), 40);
    }

    if (isTyping) {
      messagePreview = (
        <div className="flex items-center gap-1 text-blue-600">
          <div className="flex items-center gap-0.5">
            <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
            <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" style={{ animationDelay: "0.2s" }} />
            <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" style={{ animationDelay: "0.4s" }} />
          </div>
          <span className="text-xs">печатает...</span>
        </div>
      );
    }

    if (!messagePreview && chat.last_message_preview) {
      lastMessage = truncatePreview(chat.last_message_preview, 40);
    }

    return (
      <div
        key={chat.id}
        onClick={() => onSelectChat(chat)}
        className={cn(
          "cursor-pointer p-3 pr-2 flex ease-out relative",
          isSelected ? "bg-gradient-to-r from-blue-50/80 to-indigo-50/80 shadow-sm" : "hover:bg-gray-50/80",
        )}
      >
        <div className="relative flex-shrink-0">
          <Avatar className="h-12 w-12 border-2 border-white shadow-md ring-2 ring-offset-2 ring-transparent transition-all duration-300">
            <AvatarImage src={chat.contact?.avatar} alt={chat.contact?.name || chat.name} className="object-cover" />
            <AvatarFallback className={cn("bg-gradient-to-br text-lg font-medium", isSelected ? "from-blue-100 to-blue-200 text-blue-700" : "from-gray-100 to-gray-200 text-gray-600")}>
              {(chat.contact?.name || chat.name || "?")[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {isOnline && <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-green-500 border-2 border-white shadow-sm" />}
        </div>

        <div className="ml-3 flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className={cn("font-semibold text-sm truncate", isSelected ? "text-blue-900" : "text-gray-900")}>
                {chat.contact?.name || chat.name || "Без имени"}
              </span>
              {channelLabel && (
                <Badge
                  variant="outline"
                  title={chat.channel_name || channelLabel}
                  className={cn(
                    "h-5 max-w-[130px] px-1.5 text-[10px] font-medium truncate flex-shrink-0",
                    isSelected
                      ? "border-blue-200 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                  )}
                >
                  <span className="truncate">{channelLabel}</span>
                </Badge>
              )}
              {relatedChatsCount > 1 && (
                <Badge
                  variant="outline"
                  className={cn(
                    "h-5 px-1.5 text-[10px] font-medium whitespace-nowrap flex-shrink-0",
                    isSelected
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-emerald-100 bg-emerald-50/80 text-emerald-700"
                  )}
                >
                  {`Чатов: ${relatedChatsCount}`}
                </Badge>
              )}
            </div>
            <span className="text-xs text-gray-400 flex-shrink-0">{lastMessageTime}</span>
          </div>
          <div className="flex items-center justify-between mt-0.5 gap-1">
            <div className="text-xs text-gray-500 truncate flex-1">{messagePreview || lastMessage}</div>
            {isUnread && (
              <Badge className="bg-blue-500 text-white text-xs h-5 min-w-[20px] flex items-center justify-center px-1.5 flex-shrink-0">
                {chat.unread_count > 99 ? "99+" : chat.unread_count}
              </Badge>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filteredChannels = allChannels.filter(channel =>
    channel.name.toLowerCase().includes(channelsSearchQuery.toLowerCase()) ||
    (channel.description && channel.description.toLowerCase().includes(channelsSearchQuery.toLowerCase())) ||
    channel.type.toLowerCase().includes(channelsSearchQuery.toLowerCase())
  );
  const isChannelsLoading = channelsLoading || loadingChannels;

  return (
    <div className="flex flex-col h-full border-r bg-white shadow-inner">
      
      <div className="p-4 border-b bg-gradient-to-r from-white to-gray-50">
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={activeTab === "chats" ? "Поиск чатов по имени или телефону" : "Поиск каналов..."}
                value={activeTab === "chats" ? localSearch : channelsSearchQuery}
                onChange={(e) => activeTab === "chats" ? setLocalSearch(e.target.value) : setChannelsSearchQuery(e.target.value)}
                className="pl-10 bg-white border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
              />
            </div>
            {activeTab === "chats" && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const newValue = !soundEnabled;
                    setSoundEnabled(newValue);
                    localStorage.setItem("chat_sound_notifications", newValue.toString());
                  }}
                  className={`border-gray-300 transition-all duration-200 ${soundEnabled ? "text-gray-900 hover:text-blue-600 hover:border-blue-500 hover:bg-blue-50" : "text-gray-400"}`}
                  title={soundEnabled ? "Звуковые уведомления включены" : "Звуковые уведомления выключены"}
                >
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleFilterClick}
                  className="border-gray-300 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </>
            )}
            {activeTab === "channels" && (
              <Button variant="outline" size="icon" onClick={() => setIsAddChannelOpen(true)} className="border-gray-300 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200">
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="flex space-x-2">
            <Button variant={activeTab === "chats" ? "link" : "outline"} size="sm" className="flex-1" onClick={() => setActiveTab("chats")}>
              <MessageCircle className="w-4 h-4 mr-2" />Чаты
            </Button>
            <Button variant={activeTab === "channels" ? "link" : "outline"} size="sm" className="flex-1" onClick={() => setActiveTab("channels")}>
              <Hash className="w-4 h-4 mr-2" />Каналы
            </Button>
          </div>
        </div>
      </div>

      {activeTab === "chats" && renderActiveFilters()}

      {activeTab === "chats" ? (
        <div className="px-4 py-2.5 border-b bg-gray-50/50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Все чаты</span>
            <Badge variant="outline" className="font-medium bg-white border-gray-300 text-gray-700 shadow-sm">{loading ? "..." : chats.length}</Badge>
          </div>
        </div>
      ) : (
        <div className="px-4 py-2.5 border-b bg-gray-50/50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Все каналы</span>
            <Badge variant="outline" className="font-medium bg-white border-gray-300 text-gray-700 shadow-sm">{isChannelsLoading ? "..." : allChannels.length}</Badge>
          </div>
        </div>
      )}

      {activeTab === "chats" ? (
        loading ? (
          <div className="flex-1 p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-start space-x-3 animate-pulse">
                <Skeleton className="h-12 w-12 rounded-full bg-gradient-to-r from-gray-200 to-gray-300" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-1/3 bg-gradient-to-r from-gray-200 to-gray-300" />
                  <Skeleton className="h-3 w-2/3 bg-gradient-to-r from-gray-200 to-gray-300" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="divide-y divide-gray-100">
              {chats.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                  <div className="rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 p-5 mb-5 shadow-inner">
                    <MessageSquare className="h-10 w-10 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Чаты не найдены</h3>
                  <p className="text-sm text-gray-500 mb-6 max-w-[220px]">Попробуйте изменить параметры поиска или фильтры</p>
                </div>
              ) : (
                chats.map(renderChatItem)
              )}
            </div>
          </ScrollArea>
        )
      ) : (
        isChannelsLoading ? (
          <div className="flex-1 p-4 space-y-3 max-w-[400px]">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse p-3">
                <Skeleton className="h-12 w-12 rounded-md bg-gradient-to-r from-gray-200 to-gray-300" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-1/2 bg-gradient-to-r from-gray-200 to-gray-300" />
                  <Skeleton className="h-3 w-1/3 bg-gradient-to-r from-gray-200 to-gray-300" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredChannels.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 p-5 mb-5 shadow-inner">
              <Hash className="h-10 w-10 text-blue-400" />
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">
              {channelsSearchQuery ? "Каналы не найдены" : "Нет подключённых каналов"}
            </h3>
            <p className="text-sm text-gray-500 mb-6 max-w-[220px]">
              {channelsSearchQuery ? `По запросу "${channelsSearchQuery}" ничего не найдено` : "Создайте первый канал для начала работы"}
            </p>
            <Button onClick={() => setIsAddChannelOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />Подключить канал
            </Button>
          </div>
        ) : (
          <ScrollArea className="flex-1 max-w-[400px]">
            <div className="divide-y divide-gray-100 max-w-[400px]">
              {filteredChannels.map((channel) => (
                <div key={channel.id} className="p-3 border-b flex items-start gap-3 transition-all duration-300 ease-out hover:bg-gray-50/80 cursor-default">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-md bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                      {channel.type === "TELEGRAM" ? (
                        <div
                          className="w-9 h-9 [&_svg]:w-full [&_svg]:h-full [&_svg]:text-blue-600"
                          dangerouslySetInnerHTML={{ __html: TELEGRAM_SVG }}
                        />
                      ) : channel.type === "AVITO" ? (
                        <img
                          src="https://logo-teka.com/wp-content/uploads/2025/06/avito-logo.svg"
                          alt="Avito"
                          className="w-12 h-12"
                        />
                      ) : (
                        <Hash className="h-6 w-6 text-blue-600" />
                      )}
                    </div>
                  </div>
                  <div className="max-w-[320px]">
                    <div className="flex justify-between items-start gap-2 mb-1.5">
                      <div className="flex items-center gap-2 max-w-[70%] ">
                        <p className="font-semibold w-[35%] text-sm truncate text-gray-900">{channel.name}</p>
                        {channel.is_active ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><Eye className="w-3 h-3 mr-1" />Активен</Badge>
                        ) : (
                          <Badge variant="secondary"><EyeOff className="w-3 h-3 mr-1" />Неактивен</Badge>
                        )}
                      </div>
                      <div className="flex gap-1 flex-1">
                        
                        <Button variant="outline" size="sm" onClick={() => handleEditClick(channel)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Удаление канала</AlertDialogTitle>
                              <AlertDialogDescription>Вы уверены, что хотите удалить канал "{channel.name}"? Это действие нельзя отменить.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Отмена</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteChannel(channel.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Удалить</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    {channel.description && <p className="text-sm text-gray-600 leading-tight">{channel.description}</p>}
                    <div className="flex items-center gap-3 mt-2">
                      <Badge variant="outline" className="text-xs">{channel.type}</Badge>
                      <span className="text-xs text-gray-500">
                        Создан: {channel.created_at ? format(parseISO(channel.created_at), "dd.MM.yyyy") : "Нет данных"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )
      )}

      
      <Dialog open={filterModalVisible} onOpenChange={setFilterModalVisible}>
        <DialogContent className="sm:max-w-[580px] bg-white p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-semibold text-gray-900">Фильтры чатов</DialogTitle>
            <DialogDescription className="text-gray-600">Настройте параметры фильтрации</DialogDescription>
          </DialogHeader>
          <Form {...filterForm}>
            <form onSubmit={filterForm.handleSubmit(applyFilters)} className="space-y-4 px-6 pb-6">
              <FormField control={filterForm.control} name="channel_id" render={({ field }) => (
                <FormItem>
                  <FormLabel>Канал</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl><SelectTrigger><SelectValue placeholder="Все каналы" /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value="">Все каналы</SelectItem>
                      {allChannels.map(ch => <SelectItem key={ch.id} value={String(ch.id)}>{ch.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={filterForm.control} name="status" render={({ field }) => (
                <FormItem>
                  <FormLabel>Статус</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl><SelectTrigger><SelectValue placeholder="Все статусы" /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value="">Все статусы</SelectItem>
                      <SelectItem value="OPEN">Открытый</SelectItem>
                      <SelectItem value="CLOSED">Закрытый</SelectItem>
                      <SelectItem value="PENDING">Ожидающий</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={filterForm.control} name="sort_by" render={({ field }) => (
                <FormItem>
                  <FormLabel>Сортировать по</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value="last_message_time">Последнему сообщению</SelectItem>
                      <SelectItem value="created_at">Дате создания</SelectItem>
                      <SelectItem value="updated_at">Дате обновления</SelectItem>
                      <SelectItem value="name">Имени</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={filterForm.control} name="sort_order" render={({ field }) => (
                <FormItem>
                  <FormLabel>Порядок сортировки</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value="desc">По убыванию</SelectItem>
                      <SelectItem value="asc">По возрастанию</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
              <DialogFooter className="pt-6 bg-gray-50/50">
                <Button type="button" variant="outline" onClick={resetFilters}>Сбросить</Button>
                <Button type="button" variant="outline" onClick={() => setFilterModalVisible(false)}>Отмена</Button>
                <Button type="submit">Применить</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddChannelOpen} onOpenChange={setIsAddChannelOpen}>
  <DialogContent className="sm:max-w-[500px]"> 
    <DialogHeader>
      <DialogTitle>Подключение канала</DialogTitle>
      <DialogDescription>Выберите тип канала и введите данные для подключения</DialogDescription>
    </DialogHeader>
    <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto"> 
      <div className="flex gap-2">
        <Button variant={channelType === "avito" ? "default" : "outline"} onClick={() => setChannelType("avito")} className="flex-1">Avito</Button>
        <Button variant={channelType === "telegram" ? "default" : "outline"} onClick={() => setChannelType("telegram")} className="flex-1">Telegram</Button>
      </div>

      {channelType === "avito" ? (
        !showOAuthView ? (
          <>
            <div className="space-y-2 px-1"><Label>Client ID</Label><Input value={channelForm.client_id} onChange={(e) => setChannelForm({ ...channelForm, client_id: e.target.value })} placeholder="client_id" className="w-full" /></div>
            <div className="space-y-2 px-1"><Label>Client Secret</Label><Input type="password" value={channelForm.client_secret} onChange={(e) => setChannelForm({ ...channelForm, client_secret: e.target.value })} placeholder="client_secret" className="w-full" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOAuthView(true)}>Использовать OAuth</Button>
              <Button onClick={handleAddChannel}>Добавить канал</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="text-center space-y-4">
              <p className="text-sm text-gray-600">Нажмите кнопку для авторизации на Avito.</p>
              <Button onClick={handleOAuthConnect} className="w-full">Подключить через OAuth</Button>
              <Button variant="outline" onClick={() => setShowOAuthView(false)} className="w-full">Назад</Button>
            </div>
          </>
        )
      ) : (
        <>
          <div className="space-y-2 px-1">
            <Label>Название канала <span className="text-gray-400">(необязательно)</span></Label>
            <Input value={channelForm.name} onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })} placeholder="Например: Telegram поддержка" className="w-full" />
          </div>
          <div className="space-y-2 px-1">
            <Label>Bot Token <span className="text-red-500">*</span></Label>
            <Input
              type="password"
              value={channelForm.bot_token}
              onChange={(e) => setChannelForm({ ...channelForm, bot_token: e.target.value })}
              placeholder="1234567890:AAF..."
              className="w-full"
            />
            <p className="text-xs text-gray-500 break-words">
              Получите токен у{" "}
              <a
                href="https://t.me/botfather?start=newbot" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
                onClick={(e) => {
                  
                }}
              >
                @BotFather
              </a>
              {" "}командой /newbot или /token
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsAddChannelOpen(false)} className="w-full sm:w-auto">Отмена</Button>
            <Button onClick={handleAddTelegramChannel} disabled={connectingTelegram} className="w-full sm:w-auto">
              {connectingTelegram ? (
                <span className="flex items-center gap-2 justify-center">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Подключение...
                </span>
              ) : (
                <>
                  <Bot className="h-4 w-4 mr-2" />
                  Подключить Telegram
                </>
              )}
            </Button>
          </DialogFooter>
        </>
      )}
    </div>
  </DialogContent>
</Dialog>
      
      <Dialog open={isEditChannelOpen} onOpenChange={setIsEditChannelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редактирование канала</DialogTitle>
            <DialogDescription>Измените информацию о канале</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Название канала *</Label><Input value={channelForm.name} onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })} placeholder="Введите название канала" /></div>
            <div className="space-y-2"><Label>Тип канала *</Label><Input value={channelForm.type} onChange={(e) => setChannelForm({ ...channelForm, type: e.target.value })} placeholder="Например: telegram, whatsapp, vk" /></div>
            <div className="space-y-2"><Label>Описание</Label><Input value={channelForm.description} onChange={(e) => setChannelForm({ ...channelForm, description: e.target.value })} placeholder="Краткое описание канала" /></div>
            <div className="space-y-2"><Label>SVG иконка</Label><Textarea value={channelForm.svg_icon || ""} onChange={(e) => setChannelForm({ ...channelForm, svg_icon: e.target.value })} placeholder='<svg width="24" height="24"...>...</svg>' rows={6} className="font-mono text-xs" /></div>
            <div className="space-y-2"><Label>Название конфигурации API</Label><Input value={channelForm.api_config_name} onChange={(e) => setChannelForm({ ...channelForm, api_config_name: e.target.value })} placeholder="Название конфигурации для API" /></div>
            <div className="flex items-center space-x-2">
              <input type="checkbox" checked={channelForm.is_active} onChange={(e) => setChannelForm({ ...channelForm, is_active: e.target.checked })} className="rounded" />
              <Label className="cursor-pointer">Канал активен</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditChannelOpen(false)}>Отмена</Button>
            <Button onClick={handleEditChannel}>Сохранить изменения</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
